<template>
    <div class="te-cart">
        <div class="te-cart-item-list"
             v-for="(item, i) in items"
             v-bind:key="item.id">
            <cart-event v-if="item.event"
                        :cart-items="item.cartItems"
                        :event="item.event"
                        :order-id="orderId"
                        :sales-channel-id="salesChannelId"
                        :register-id="registerId"
                        :customer-id="customerId"
                        :tab-index="i"
                        :disable-cart-editing="!canEdit"
                        :language="languageSettings"
                        @change="onQuantityChange"
            ></cart-event>
            <cart-product v-if="item.productDefinition"
                          :cart-items="item.cartItems"
                          :product-definition="item.productDefinition"
                          :order-id="orderId"
                          :sales-channel-id="salesChannelId"
                          :register-id="registerId"
                          :customer-id="customerId"
                          :tab-index="i"
                          :disable-cart-editing="!canEdit"
                          :language="languageSettings"
                          @change="onQuantityChange"
            ></cart-product>
        </div>
<!--        <div v-if="!editMode && order !== null" class="te-cart-total">-->
        <div v-if="order !== null" class="te-cart-total">
            <div class="te-cart-total__price-label"><span>{{languageSettings.totalPrice}}</span></div>
            <div class="te-cart-total__price">
<!--                <span>{{getOrderTotalPriceForCurrency('EUR')}}</span>-->
                <span v-for="totalPrice in carTotalPrices" :key="totalPrice">{{totalPrice}}</span>
            </div>
        </div>
<!--        <div v-if="canEdit" class="te-cart-actions">-->
<!--            <button v-if="!editMode" class="te-button te-primary-action" data-cy="change" @click="toggleEditMode">{{languageSettings.change}}</button>-->
<!--            <button v-if="editMode" class="te-button te-primary-action" data-cy="save" @click="onChange">{{languageSettings.save}}</button>-->
<!--            <button v-if="editMode" class="te-button te-secondary-action" data-cy="cancel" @click="toggleEditMode">{{languageSettings.cancel}}</button>-->
<!--        </div>-->

        <order-messages></order-messages>

        <offers v-if="canEdit" :language="language"></offers>

        <div v-if="order !== null" class="te-checkout">
            <payment-method
                    v-if="canCreateIsoCurrencyPayment"
                    :language="language"
                    @change="onSelectPaymentMethod"
            ></payment-method>
            <credit-payment-method
                    v-if="canCreateCustomCurrencyPayment"
                    v-model="creditPayments"
                    :language="language"
            ></credit-payment-method>
            <loyalty-payment-method
                    v-if="canCreateLoyaltyCardPayment"
                    v-model="loyaltyPayments"
                    :language="language"
            ></loyalty-payment-method>

            <div v-if="canFillOutEmail" class="te-cart-email" :class="{ 'te-required': !isValidEmail }">
                <div class="te-cart-heading">{{languageSettings.email}}</div>
                <input class="te-form-control" type="email" name="email" min="0" v-model="email" :placeholder="languageSettings.emailPlaceholder">
            </div>
            <opt-in v-if="canCheckoutOrder"
                    v-model="optInOn"
                    :order-id="orderId"
                    :customer-id="customerId"
                    :preferred-language-code="preferredLanguageCode"
            ></opt-in>
            <button v-if="canCheckoutOrder" class="te-button te-primary-action" data-cy="checkout" :disabled="checkoutDisabled" @click="onCheckout">{{languageSettings.checkout}}</button>
            <button v-if="!canCheckoutOrder && canCreatePayment && !canFinishPayment" class="te-button te-primary-action" data-cy="pay" @click="onCheckout">{{languageSettings.pay}}</button>
            <button v-if="canFinishPayment" class="te-button te-primary-action" data-cy="pay" @click="onFinishPayment">{{languageSettings.pay}}</button>
            <button v-if="canReserve" class="te-button te-primary-action" data-cy="reserve" :disabled="reservationDisabled" @click="onReserve">{{languageSettings.reserve}}</button>
        </div>

        <div v-if="cartMessage" class="te-cart-message">
            <span>{{cartMessage}}</span>
        </div>
        <button v-if="isTimeoutOrder" class="te-button te-primary-action" @click="cancelOrder">{{languageSettings.cancelOrder}}</button>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from "vuex";
    import CartEvent from './CartEvent'
    import CartProduct from './CartProduct'
    import PaymentMethod from './PaymentMethod'
    import CreditPaymentMethod from './CreditPaymentMethod'
    import LoyaltyPaymentMethod from './LoyaltyPaymentMethod'
    import OptIn from './OptIn'
    import OrderMessages from './OrderMessages';
    import Offers from './Offers';
    import format from '../services/format'
    import lookup from '../services/lookup'
    import {
        CanCheckout, CanPay, CanPayOnline,
        HasItemsWithStatus,
        IsCanceled, IsCheckedOut,
        IsCompleted, IsEmpty, IsInFinalState, IsPending,
        IsProcessingPayment,
        IsTimeout, NeedsLoyaltyCardPayment, NeedsPaymentWithCustomCurrency, NeedsPaymentWithIsoCurrency
    } from "ticketengine-checkout-sdk";

    export default {
        name: "Cart",

        components: {
            CartEvent,
            CartProduct,
            CreditPaymentMethod,
            LoyaltyPaymentMethod,
            PaymentMethod,
            OptIn,
            OrderMessages,
            Offers,
        },

        data() {
            return {
                c: false,
                cartItemMutations: {},
                email: null,
                paymentMethod: null,
                creditPayments: [],
                loyaltyPayments: [],
                optInOn: [],
            };
        },

        props: {
            orderId: {
                type: String,
            },
            salesChannelId: {
                type: String,
                required: true
            },
            registerId: {
                type: String,
                required: true
            },
            disablePaymentMethodSelection: {
                type: Boolean,
                default: false
            },
            disableCartEditing: {
                type: Boolean,
                default: false
            },
            customerId: {
                type: String
            },
            preferredLanguageCode: {
                type: String
            },
            language: {
                type: Object
            },
        },

        computed: {
            ...mapGetters({
                error: 'error',
                order: 'order',
            }),

            items: function() {
                const items = [];
                if(this.order && this.order.lineItems) {
                    this.order.lineItems.forEach(lineItem => {
                        // event items
                        if(lineItem.type === 'access' && (lineItem.status !== 'removed' && lineItem.status !== 'returned')) {
                            const item = items.find(i => i.event && i.event.id === lineItem.event.id);
                            if(item && item.cartItems) {
                                // event is already in items
                                const cartItem = item.cartItems.find(i => i.requestedConditionPath.join('.') === lineItem.requestedConditionPath.join('.'));
                                if(cartItem && cartItem.lineItemIds) {
                                    // same price is already in cartItems
                                    cartItem.lineItemIds.push(lineItem.id);
                                } else {
                                    // price is not in cartItems
                                    item.cartItems.push(this.getCartItem(lineItem));
                                }
                            } else {
                                // event is not in items
                                items.push({
                                    event: lineItem.event,
                                    cartItems: [this.getCartItem(lineItem)],
                                })
                            }
                        }

                        // product items
                        if(lineItem.type === 'product' && (lineItem.status !== 'removed' && lineItem.status !== 'returned')) {
                            const item = items.find(i => i.productDefinition && i.productDefinition.id === lineItem.productDefinition.id);
                            if(item && item.cartItems) {
                                // product definition is already in items
                                const cartItem = item.cartItems.find(i => i.requestedConditionPath.join('.') === lineItem.requestedConditionPath.join('.'));
                                if(cartItem && cartItem.lineItemIds) {
                                    // same price is already in cartItems
                                    cartItem.lineItemIds.push(lineItem.id);
                                } else {
                                    // price is not in cartItems
                                    item.cartItems.push(this.getCartItem(lineItem));
                                }
                            } else {
                                // product definition is not in items
                                items.push({
                                    productDefinition: lineItem.productDefinition,
                                    cartItems: [this.getCartItem(lineItem)],
                                })
                            }
                        }
                    });
                }
                return items;
            },

            carTotalPrices: function() {
                if(this.order && this.order.requiredPayments) {
                    return this.order.requiredPayments.map(requiredPayment => {
                        return format.price(requiredPayment.currency, requiredPayment.amount)
                    })
                }
                return []
            },

            canReserve: function () {
                // const canReserve = new CanReserve();
                const isPending = new IsPending();
                const isEmpty = new IsEmpty();
                const isInFinalState = new IsInFinalState();
                const hasPendingItems = new HasItemsWithStatus(['pending', 'awaitingClaim']);
                return !this.canPayOnline
                    // && !canReserve.validate(this.order)
                    && isPending.validate(this.order)
                    && !hasPendingItems.validate(this.order)
                    && !isInFinalState.validate(this.order)
                    && !isEmpty.validate(this.order);
            },

            canPayOnline: function () {
                const canPayOnline = new CanPayOnline();
                return canPayOnline.validate(this.order)
            },

            canEdit: function () {
                return (this.canReserve || this.canCheckoutOrder) && this.disableCartEditing === false;
            },

            canFillOutEmail: function () {
                return this.canReserve || this.canCheckoutOrder;
            },

            canCreatePayment: function () {
                const canPay = new CanPay();
                // const hasPayments = this.order && this.order.payments ? this.order.payments.length > 0 : false;
                // return canPay.validate(this.order) && !hasPayments && this.canPayOnline;
                const hasOpenPayments = this.openPayments.length > 0;
                return canPay.validate(this.order) && hasOpenPayments && this.canPayOnline;
            },

            canFinishPayment: function () {
                if(this.order && this.order.paymentStatus && this.order.paymentStatus === 'pending' && this.order.paymentUrl) {
                    return true;
                }
                return false
            },

            canCreateIsoCurrencyPayment: function () {
                const canPay = new CanPay();
                const needsPaymentWithIsoCurrency = new NeedsPaymentWithIsoCurrency();
                // return canPay.validate(this.order) && needsPaymentWithIsoCurrency.validate(this.order);
                return canPay.validate(this.order) && needsPaymentWithIsoCurrency.validate(this.order) && !this.disablePaymentMethodSelection;
            },

            canCreateCustomCurrencyPayment: function () {
                const canPay = new CanPay();
                const needsPaymentWithCustomCurrency = new NeedsPaymentWithCustomCurrency();
                return canPay.validate(this.order) && needsPaymentWithCustomCurrency.validate(this.order);
            },

            canCreateLoyaltyCardPayment: function () {
                const needsLoyaltyCardPayment = new NeedsLoyaltyCardPayment();
                return needsLoyaltyCardPayment.validate(this.order);
            },

            canCheckoutOrder: function () {
                const canCheckout = new CanCheckout();
                const isCheckedOut = new IsCheckedOut();
                return canCheckout.validate(this.order) && this.canPayOnline && !isCheckedOut.validate(this.order);
            },

            checkoutDisabled: function () {
                // return (this.paymentMethod === null && (this.order && this.order.paymentStatus !== 'paid')) || !this.isValidEmail;
                const isPaid = this.order && this.order.paymentStatus === 'paid';
                const needsToSelectPaymentMethod = this.canCreateIsoCurrencyPayment && this.paymentMethod === null;
                const needsToSetAccountTokens = this.canCreateCustomCurrencyPayment && this.creditPayments.filter(cp => cp.token === null || cp.token === '').length > 0;
                const needsToSupplyLoyaltyCard = this.canCreateLoyaltyCardPayment && this.loyaltyPayments.filter(lp => lp.cardId === null || lp.cardId === '').length > 0;
                return (!isPaid && needsToSelectPaymentMethod) || (!isPaid && needsToSetAccountTokens) || needsToSupplyLoyaltyCard || !this.isValidEmail;
            },

            reservationDisabled: function () {
                return !this.isValidEmail;
            },

            isTimeoutOrder: function () {
                const isTimeout = new IsTimeout();
                return isTimeout.validate(this.order);
            },

            // isReservedOrder: function () {
            //     const isTimeout = new IsTimeout();
            //     return isTimeout.validate(this.order);
            // },

            isValidEmail: function () {
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return re.test(String(this.email).toLowerCase());
            },

            // return list of payments that have not been created yet
            openPayments: function () {
                const payments = this.order && this.order.payments ? this.order.payments.filter(p => !['refused', 'cancelled'].includes(p.status) ) : [];
                if(this.order && this.order.requiredPayments) {
                    return this.order.requiredPayments.filter(rq => {
                        const paidAmount = payments.filter(p => p.currency.code === rq.currency.code).reduce((total, p) => { return total + p.amount }, 0);
                        return paidAmount < rq.amount;
                    });
                }
                return [];
            },

            cartMessage: function () {
                const isCanceled = new IsCanceled();
                const isCompleted = new IsCompleted();
                // const isCheckedOut = new IsCheckedOut();
                const isProcessingPayment = new IsProcessingPayment();
                const isEmpty = new IsEmpty();
                const hasPendingItems = new HasItemsWithStatus(['pending', 'awaitingClaim']);
                const hasOpenPayments = this.openPayments.length > 0;

                // if(this.order === null) return this.languageSettings.orderNotFound;
                if(this.order === null) return this.languageSettings.emptyCart;
                if(this.order && this.order.status === 'redirectToPayment') return this.languageSettings.redirectToPayment;
                if(this.isTimeoutOrder) return this.languageSettings.orderTimeout;
                if(isCanceled.validate(this.order)) return this.languageSettings.orderCanceled;
                if(isCompleted.validate(this.order)) return this.languageSettings.orderCompleted;
                // if(this.order && this.order.status === 'reserved') return this.languageSettings.orderReserved;
                if(isProcessingPayment.validate(this.order) && !hasOpenPayments) return this.languageSettings.paymentProcessing;
                if(isEmpty.validate(this.order)) return this.languageSettings.noItems;
                if(hasPendingItems.validate(this.order)) return this.languageSettings.pendingItems;
                return null;
            },


            languageSettings: function() {
                const defaultSettings = {
                    change: 'Change',
                    cancel: 'Cancel',
                    save: 'Save',
                    totalPrice: 'Total',
                    checkout: 'Checkout',
                    reserve: 'Reserve',
                    pay: 'Pay',
                    free: 'Free',
                    email: 'Email',
                    emailPlaceholder: 'my.email@example.com',
                    emptyCart: 'Cart is empty.',
                    orderNotFound: 'Order not found.',
                    orderTimeout: 'Order timeout.',
                    orderCanceled: 'Order canceled.',
                    orderCompleted: 'Order completed.',
                    orderReserved: 'Order reserved.',
                    redirectToPayment: 'Redirect to payment page. One moment please.',
                    noItems: 'Order is empty.',
                    pendingItems: 'Order has unconfirmed tickets.',
                    paymentProcessing: 'Processing payment.',
                    cancelOrder: 'Cancel order',
                };
                return Object.assign(defaultSettings, this.language);
            },
        },

        methods: {
            ...mapActions([
                "getOrder",
                "getOrderMessages",
                "getOffers",
                "clearOrder",
                "changeCart",
                "checkout",
                "reserve",
            ]),

            getCartItem(lineItem) {
                if(lineItem.accessDefinition) {
                    let limit = lineItem.limit;
                    if(lineItem.event && lineItem.event.availableCapacity && lineItem.event.availableCapacity < limit) limit = lineItem.event.availableCapacity;
                    // const limit = (lineItem.limit && lineItem.event && lineItem.event.availableCapacity && lineItem.limit <= lineItem.event.availableCapacity) ? lineItem.limit : lineItem.event.availableCapacity;
                    return {
                        id: lineItem.id,
                        name: lineItem.name,
                        status: lineItem.status,
                        currency: lineItem.currency,
                        price: lineItem.price,
                        limit: limit,
                        requestedConditionPath: lineItem.requestedConditionPath,
                        accessDefinitionId: lineItem.accessDefinition.id,
                        // accessIds: [lineItem.accessId],
                        lineItemIds: [lineItem.id],
                    }
                } else if(lineItem.productDefinition) {
                // } else if(lineItem.productDefinition && !['removed', 'returned'].includes(lineItem.status)) {
                    return {
                        id: lineItem.id,
                        name: lineItem.name,
                        status: lineItem.status,
                        currency: lineItem.currency,
                        price: lineItem.price,
                        limit: lineItem.limit,
                        requestedConditionPath: lineItem.requestedConditionPath,
                        productDefinitionId: lineItem.productDefinition.id,
                        lineItemIds: [lineItem.id],
                    }
                }
                return null;
            },

            // orderIsEmpty(order) {
            //     // return order.lineItems.filter((item) => item.status === 'reserved').length === 0;
            //     return check.orderIsEmpty(order);
            // },

            // hasPendingOrderLineItems(order) {
            //     // return order.lineItems.filter((item) => item.status === 'pending' || item.status === 'awaitingClaim').length > 0;
            //     // return order.lineItems.filter((item) => item.status === 'pending' || item.status === 'awaitingClaim').length === 0;
            //     return check.orderHasPendingOrderLineItems(order);
            // },

            cancelOrder() {
                this.clearOrder();
            },

            // toggleEditMode() {
            //     this.editMode = !this.editMode;
            // },

            onQuantityChange({cartItemId, commands}) {
                this.cartItemMutations[cartItemId] = commands;
                this.onChange();
            },

            // getOrderTotalPriceForCurrency(currencyCode) {
            //     const price = this.order.requiredPayments.reduce((total, requiredPayment) => {
            //         if(requiredPayment.currency === currencyCode) {
            //             return total + requiredPayment.amount;
            //         }
            //         return total;
            //     }, 0);
            //
            //     // if(price === 0) {
            //     //     return this.languageSettings.free;
            //     // }
            //     return format.price(currencyCode, price);
            // },


            onChange() {
                const [addItems, removeItems] = this.getChangeItems();
                this.changeCart({addItems, removeItems});
                // this.toggleEditMode();
                this.cartItemMutations = {}; // clear changes

                // let commands = [];
                // Object.keys(this.cartItemMutations).forEach(cartItemId => {
                //     commands = commands.concat(this.cartItemMutations[cartItemId])
                // });
                // this.changeCart({mutations: commands, orderId: this.orderId});
            },

            getChangeItems() {
                let commands = [];
                Object.keys(this.cartItemMutations).forEach(cartItemId => {
                    commands = commands.concat(this.cartItemMutations[cartItemId])
                });

                const addItems = [];
                const removeItems = [];
                commands.forEach(command => {
                    if(command.type && command.type === 'AddAccessToCart') {
                        addItems.push({
                            eventManagerId: command.data.eventManagerId,
                            eventId: command.data.eventId,
                            accessDefinitionId: command.data.accessDefinitionId,
                            requestedConditionPath: command.data.requestedConditionPath,
                            capacityLocationPath: command.data.capacityLocationPath || null,
                        });
                    }
                    if(command.type && command.type === 'AddProductToCart') {
                        addItems.push({
                            productDefinitionId: command.data.productDefinitionId,
                            requestedConditionPath: command.data.requestedConditionPath,
                        });
                    }
                    if(command.type && command.type === 'RemoveItemFromCart') {
                        removeItems.push({orderLineItemId: command.data.orderLineItemId});
                    }
                });

                return [addItems, removeItems]
            },

            onSelectPaymentMethod(method) {
                this.paymentMethod = method;
            },

            onCheckout() {
                // const payments = this.order.requiredPayments ? this.order.requiredPayments.map((p) => {
                const payments = this.openPayments ? this.openPayments.map((p) => {
                    const isIsoCurrency = p.currency.code.length === 3;
                    const accountToken = this.creditPayments.find(cp => cp.currency.code === p.currency.code);
                    return {
                        currencyCode: p.currency.code,
                        amount: p.amount,
                        method: isIsoCurrency ? this.paymentMethod : null,
                        token: accountToken && accountToken.token ? accountToken.token : null
                    }
                }) : null;
                if(this.loyaltyPayments && this.loyaltyPayments.length > 0) {
                    this.loyaltyPayments.forEach((lp) => {
                        payments.push({
                            currencyCode: lp.currency.code,
                            amount: lp.amount,
                            loyaltyCardType: lp.cardType,
                            loyaltyCardId: lp.cardId,
                            loyaltyCardPin: lp.cardPin,
                        });
                    });
                }
                this.checkout({
                    payments: payments,
                    email: this.email,
                    optInOn: this.optInOn
                });
            },

            onReserve() {
                this.reserve({
                    email: this.email,
                    timeoutOn: lookup.getEarliestEventStart(this.order),
                });
            },

            onFinishPayment() {
                if(this.order && this.order.paymentUrl) {
                    window.location.href = this.order.paymentUrl;
                }
            },

            setCreditPayments() {
                // if(this.order && this.order.requiredPayments) {
                //     this.creditPayments = this.order.requiredPayments.filter(rp => rp.currency.code.length > 3).map(rp => {
                //         rp.token = null;
                //         return rp;
                //     })
                // }
                if(this.order && this.order.requiredPayments) {
                    this.creditPayments = this.openPayments.filter(rp => rp.currency.code.length > 3).map(rp => {
                        rp.token = null;
                        return rp;
                    })
                }
            },

            setLoyaltyPayments() {
                const payments = this.order && this.order.payments ? this.order.payments.filter(p => !['refused', 'cancelled'].includes(p.status) ) : [];
                if(this.order && this.order.requiredLoyaltyCardPayments) {
                    const rlp = [];
                    this.order.requiredLoyaltyCardPayments.forEach((rp) => {
                        const loyaltyCardPayments = payments.filter(p => p.currency.code === rp.currency.code && p.psp === 'loyalty' && p.method === rp.cardType);
                        const paidAmount = loyaltyCardPayments.reduce((total, p) => { return total + p.amount }, 0);
                        if(loyaltyCardPayments.length === 0 || paidAmount < rp.amount) {
                            rlp.push({
                                currency: rp.currency,
                                amount: rp.amount,
                                cardType: rp.cardType,
                                cardId: null,
                                cardPin: null,
                            });
                        }
                    });
                    this.loyaltyPayments = rlp;
                }
            },
        },

        watch: {
            orderId: async function(id) {
                await this.getOrder({orderId: id, forceReload: true});
                this.setCreditPayments();
                this.setLoyaltyPayments();
            },
            order: async function(order) {
                if(order && order.customer && order.customer.email) {
                    this.email = order.customer.email;
                }
                this.setLoyaltyPayments();
            }
        },

        async created() {
            if(this.orderId) {
                await this.getOrder({orderId: this.orderId, forceReload: true, clearOnFinalState: true});
                this.getOrderMessages({stage: 'Checkout', orderId: this.orderId, customerId: this.customerId, salesChannelId: this.salesChannelId, preferredLanguage: this.preferredLanguageCode});
                this.getOffers({orderId: this.orderId, customerId: this.customerId, salesChannelId: this.salesChannelId, preferredLanguage: this.preferredLanguageCode});
                this.setCreditPayments();
                this.setLoyaltyPayments();
            }
        }
    }
</script>

<style scoped>

</style>
